import React, { useCallback, useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { useParams } from "react-router-dom";

import ButtonV2 from "../../../components/ui/button/v2";
import { JobStatusWithSkeleton } from "../../../components/ui/loading/JobStatuswithSkeletonGrid";
import { useAppDispatch } from "../../../app/hooks";
import { getGeneratedFraudElements } from "../logic/fraudElementsThunk";
import { showErrorToast } from "../../../lib/toast";

const AUTO_REFRESH_INTERVAL_SECONDS = 60;

interface GeneratingFraudElementsStateProps {
  partyId: string;
}

const GeneratingFraudElementsState: React.FC<
  GeneratingFraudElementsStateProps
> = ({ partyId }) => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(AUTO_REFRESH_INTERVAL_SECONDS);

  const handleRefresh = useCallback(async () => {
    if (!caseId || isRefreshing) {
      return;
    }

    setIsRefreshing(true);

    try {
      await dispatch(
        getGeneratedFraudElements({ case_id: caseId, party_id: partyId }),
      ).unwrap();
    } catch {
      showErrorToast("Failed to refresh fraud elements status. Please try again.");
    } finally {
      setSecondsRemaining(AUTO_REFRESH_INTERVAL_SECONDS);
      setIsRefreshing(false);
    }
  }, [caseId, partyId, isRefreshing, dispatch]);

  useEffect(() => {
    if (secondsRemaining <= 0) {
      handleRefresh();
      return;
    }

    const timer = setTimeout(() => {
      setSecondsRemaining((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [secondsRemaining, handleRefresh]);

  return (
    <div className="flex flex-col items-center gap-7">
      <JobStatusWithSkeleton
        title="Analysing Investigation and Committee Details"
        description="Canso agent has started generating the Fraud Elements. This may take a few minutes to complete."
      />

      <div className="flex flex-col items-center gap-2">
        <ButtonV2
          variant="primary"
          size="md"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="h-9 rounded-[8px] bg-[linear-gradient(180deg,#788AF7_0%,#3551F3_100%)] text-[#FAFAFA]"
        >
          Refresh Status
        </ButtonV2>

        <div className="flex items-center gap-2 text-[#A3A3A3]">
          <Clock className="h-4 w-4" />

          <span className="text-xs leading-4">
            {`Auto-refreshing in ${secondsRemaining}s...`}
          </span>
        </div>
      </div>
    </div>
  );
};

export default GeneratingFraudElementsState;